// ProlongationModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FileText, X, Clock, CheckCircle, AlertCircle, Calendar, User, File, Download, ChevronDown, ChevronUp, FileIcon, Eye } from 'lucide-react';
import axios from 'axios';
import { BASE_URL } from '../../../config/axios';
import { toast } from 'react-toastify';
import './prolongationModal.css';
import { AddProlongation, DeleteProlongation, UpdateProlongation } from './ProlongationCRUD';
import { DeleteDocuments } from '../ProjectDetailsCrudButtons';
import JSZip from 'jszip';

const ProlongationModal = ({ open, onClose, project, contractor, data }) => {
    const [prolongationData, setProlongationData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [activeFilter, setActiveFilter] = useState('All');
    const [loading, setLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingStage, setLoadingStage] = useState('');
    const [expandedRemarks, setExpandedRemarks] = useState({});
    const [expandedDocs, setExpandedDocs] = useState({});
    const [expandedDesc, setExpandedDesc] = useState({});
    const tableRef = useRef(null);
    const docRefs = useRef({});
    const [downloadingZip, setDownloadingZip] = useState({});
    const [downloadingDoc, setDownloadingDoc] = useState({});

    const fetchProlongationData = async () => {
        try {
            setLoadingStage('Fetching prolongation records...');
            setLoadingProgress(50);

            const res = await axios.get(
                `${BASE_URL}/siteproject/prolongation-deviationfilter/?project=${project}`
            );
            const data = Array.isArray(res.data)
                ? res.data
                : Array.isArray(res.data?.results)
                    ? res.data.results
                    : Array.isArray(res.data?.data)
                        ? res.data.data
                        : [];
            setProlongationData(data);
            setFilteredData(data);
            setLoadingProgress(100);
            return true;
        } catch (error) {
            toast.error('Failed to fetch prolongation data');
            console.error('Error fetching prolongation data:', error);
            return false;
        }
    };

    const fetchAllData = async () => {
        setLoading(true);
        setLoadingProgress(0);
        setLoadingStage('Initializing...');

        try {
            await new Promise(resolve => setTimeout(resolve, 100));
            setLoadingProgress(20);
            setLoadingStage('Loading prolongation records...');

            const success = await fetchProlongationData();

            if (!success) {
                setLoading(false);
                return;
            }

            setLoadingProgress(100);
            setTimeout(() => {
                setLoading(false);
            }, 300);

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load prolongation data');
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchAllData();
        }
    }, [open]);

    useEffect(() => {
        if (open) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    if (!open || !project) return null;

    // Filter handler
    const handleFilter = (filterType) => {
        setActiveFilter(filterType);

        if (filterType === 'All') {
            setFilteredData(prolongationData);
        } else {
            const filtered = prolongationData.filter(
                item => item.comment_type === filterType
            );
            setFilteredData(filtered);
        }

        // Close all expanded sections when filtering
        setExpandedRemarks({});
        setExpandedDocs({});
        setExpandedDesc({});

        // Scroll to table top
        if (tableRef.current) {
            setTimeout(() => {
                tableRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }, 100);
        }
    };

    // Toggle remark expansion
    const toggleRemark = (id) => {
        setExpandedRemarks(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // Toggle document expansion and scroll
    const toggleDocs = (id) => {
        const newState = !expandedDocs[id];
        setExpandedDocs(prev => ({
            ...prev,
            [id]: newState
        }));

        // Scroll to the document section if expanding
        if (newState && docRefs.current[id]) {
            setTimeout(() => {
                docRefs.current[id]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }, 100);
        }
    };

    // Toggle description expansion
    const toggleDesc = (id) => {
        setExpandedDesc(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // Group by comment type
    const groupByCommentType = () => {
        const groups = {};
        const dataToGroup = filteredData || prolongationData;

        dataToGroup?.forEach(item => {
            const type = item.comment_type || 'Other';
            if (!groups[type]) groups[type] = [];
            groups[type].push(item);
        });

        return groups;
    };

    const typeGroups = groupByCommentType();
    const totalComments = filteredData?.length || 0;

    // Calculate stats from original data (not filtered)
    const prolongationCount = prolongationData.filter(item => item.comment_type === 'Prolongation').length;
    const deviationCount = prolongationData.filter(item => item.comment_type === 'Deviation').length;
    const otherCount = prolongationData.filter(item => !item.comment_type || item.comment_type === 'Other').length;

    const LoadingSpinner = () => (
        <div className="plm-loading-overlay">
            <div className="plm-loading-container">
                <div className="plm-loading-spinner">
                    <div className="plm-spinner-ring"></div>
                </div>
                <div className="plm-loading-progress">
                    <div className="plm-progress-bar-container">
                        <div
                            className="plm-progress-bar-fill"
                            style={{ width: `${loadingProgress}%` }}
                        ></div>
                    </div>
                    <div className="plm-progress-percentage">{loadingProgress}%</div>
                </div>
                <div className="plm-loading-stage">{loadingStage}</div>
                <div className="plm-loading-message">
                    Loading prolongation records for {data?.project_short_name}...
                </div>
            </div>
        </div>
    );


    const getFileExtension = (url) => {
        if (!url) return '';
        const cleanUrl = url.split('?')[0];
        const parts = cleanUrl.split('.');
        return parts.length > 1 ? parts.pop() : '';
    };

    const handleDownloadSingleDocument = async (doc) => {
        if (!doc.document_file) {
            toast.error('No file available to download');
            return;
        }

        setDownloadingDoc(prev => ({ ...prev, [doc.id]: true }));
        const loadingToastId = toast.loading('Downloading...');

        try {
            const res = await axios.get(doc.document_file, { responseType: 'blob' });
            const ext = getFileExtension(doc.document_file) || 'pdf';
            const baseName = (doc.document_name || 'document').replace(/[\\/:*?"<>|]/g, '_');
            const fileName = baseName.toLowerCase().endsWith(`.${ext}`) ? baseName : `${baseName}.${ext}`;

            const blobUrl = window.URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            toast.dismiss(loadingToastId);
            toast.success('Downloaded successfully');
        } catch (err) {
            toast.dismiss(loadingToastId);
            toast.error('Failed to download document');
            console.error(err);
        } finally {
            setDownloadingDoc(prev => ({ ...prev, [doc.id]: false }));
        }
    };

    const handleDownloadAllDocuments = async (item) => {
        const docs = item.documents?.filter(d => d.document_file) || [];
        if (docs.length === 0) {
            toast.info('No documents available to download');
            return;
        }

        setDownloadingZip(prev => ({ ...prev, [item.id]: true }));
        const loadingToastId = toast.loading(`Preparing zip (0/${docs.length})...`);

        try {
            const zip = new JSZip();
            let completed = 0;

            await Promise.all(
                docs.map(async (doc, idx) => {
                    try {
                        const res = await axios.get(doc.document_file, { responseType: 'blob' });
                        const ext = getFileExtension(doc.document_file) || 'pdf';
                        const baseName = (doc.document_name || `document_${idx + 1}`).replace(/[\\/:*?"<>|]/g, '_');
                        const fileName = baseName.toLowerCase().endsWith(`.${ext}`) ? baseName : `${baseName}.${ext}`;
                        zip.file(fileName, res.data);
                    } catch (err) {
                        console.error('Failed to fetch document:', doc.document_file, err);
                    } finally {
                        completed += 1;
                        toast.update(loadingToastId, { render: `Preparing zip (${completed}/${docs.length})...` });
                    }
                })
            );

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${(item.project_name || 'Project').replace(/\s+/g, '_')}_${item.comment_type || 'Documents'}_${item.id.slice(0, 8)}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.dismiss(loadingToastId);
            toast.success('Documents downloaded successfully');
        } catch (err) {
            toast.dismiss(loadingToastId);
            toast.error('Failed to download documents');
            console.error(err);
        } finally {
            setDownloadingZip(prev => ({ ...prev, [item.id]: false }));
        }
    };

    const modalContent = (
        <div className="plm-modal-overlay" onClick={onClose}>
            <div className="plm-modal" onClick={e => e.stopPropagation()}>
                {loading && <LoadingSpinner />}

                <div className="plm-modal-header">
                    <div className="plm-modal-title" style={{ width: "95%" }}>
                        <div className="flex-row justify-between" style={{ alignItems: 'center', width: "100%" }}>
                            <div className="flex-row " style={{ alignItems: 'center', gap: '10px' }}>
                                <div className="plm-modal-icon-wrap" style={{ background: "black", color: "white" }}>
                                    <FileText size={18} color="white" />
                                </div>
                                <div>
                                    <h3>Additional Comment Summary</h3>
                                    <p>{data?.project_short_name}</p>
                                </div>
                            </div>
                            <div>
                                <AddProlongation project={project} getData={fetchAllData} />
                            </div>
                        </div>
                    </div>
                    <button className="plm-close-btn" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {/* Stats Cards with Click Handlers */}
                <div className="plm-modal-stats">
                    <div
                        className={`plm-stats-card plm-total-card ${activeFilter === 'All' ? 'plm-active-filter' : ''}`}
                        onClick={() => handleFilter('All')}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="plm-stats-icon-wrap">📊</div>
                        <div className="plm-stats-info">
                            <div className="plm-stats-value">{prolongationData.length}</div>
                            <div className="plm-stats-label">Total</div>
                            {activeFilter === 'All' && (
                                <div className="plm-filter-indicator">✓ Active</div>
                            )}
                        </div>
                    </div>
                    <div
                        className={`plm-stats-card plm-prolongation-card ${activeFilter === 'Prolongation' ? 'plm-active-filter' : ''}`}
                        onClick={() => handleFilter('Prolongation')}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="plm-stats-icon-wrap">⏰</div>
                        <div className="plm-stats-info">
                            <div className="plm-stats-value">{prolongationCount}</div>
                            <div className="plm-stats-label">Prolongation</div>
                            {activeFilter === 'Prolongation' && (
                                <div className="plm-filter-indicator">✓ Active</div>
                            )}
                        </div>
                    </div>
                    <div
                        className={`plm-stats-card plm-deviation-card ${activeFilter === 'Deviation' ? 'plm-active-filter' : ''}`}
                        onClick={() => handleFilter('Deviation')}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="plm-stats-icon-wrap">🔄</div>
                        <div className="plm-stats-info">
                            <div className="plm-stats-value">{deviationCount}</div>
                            <div className="plm-stats-label">Deviation</div>
                            {activeFilter === 'Deviation' && (
                                <div className="plm-filter-indicator">✓ Active</div>
                            )}
                        </div>
                    </div>

                </div>

                {Object.keys(typeGroups).length > 0 ? (
                    <div className="plm-type-breakdown" ref={tableRef}>
                        <div className="plm-breakdown-header">
                            <div className="plm-breakdown-title">
                                <FileText size={14} />
                                By Comment Type
                                {activeFilter !== 'All' && (
                                    <span className="plm-filter-badge">
                                        Filtered: {activeFilter}
                                        <button
                                            className="plm-filter-clear"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFilter('All');
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </span>
                                )}
                            </div>
                            <div className="plm-breakdown-count">
                                {filteredData.length} records
                                {activeFilter !== 'All' && (
                                    <span className="plm-filter-info">
                                        (of {prolongationData.length} total)
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="plm-table-box">
                            <table className="plm-table">
                                <thead className="plm-table-head">
                                    <tr>
                                        <th className="plm-col-index">#</th>
                                        <th className="plm-col-type">Comment Type</th>
                                        <th className="plm-col-description">Description</th>
                                        <th className="plm-col-amount">Amount</th>
                                        <th className="plm-col-documents">Documents</th>
                                        <th className="plm-col-remarks">Remarks</th>
                                        <th className="plm-col-action">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData?.map((item, idx) => {
                                        const isRemarkExpanded = expandedRemarks[item.id];
                                        const isDocsExpanded = expandedDocs[item.id];
                                        const isDescExpanded = expandedDesc[item.id];
                                        const hasDocuments = item.documents && item.documents.length > 0;
                                        const hasRemarks = item.remark && item.remark.trim().length > 0;
                                        const descriptionPoints = item.description_point || [];
                                        const hasDescription = descriptionPoints.length > 0;

                                        // Check if description needs truncation (more than 2 items or long text)
                                        const needsDescToggle = descriptionPoints.length > 2 ||
                                            descriptionPoints.some(p => p.description?.length > 50);

                                        // Check if remarks need truncation (more than 100 chars)
                                        const needsRemarkToggle = item.remark?.length > 100;

                                        return (
                                            <React.Fragment key={item.id}>
                                                <tr className="plm-table-row">
                                                    <td className="plm-col-index">{idx + 1}.</td>
                                                    <td className="plm-col-type">
                                                        <span className={`plm-type-badge ${item.comment_type?.toLowerCase() || 'other'}`}>
                                                            {item.comment_type || "Other"}
                                                        </span>
                                                    </td>
                                                    <td className="plm-col-description">
                                                        {hasDescription ? (
                                                            needsDescToggle ? (
                                                                <button
                                                                    className="plm-desc-toggle-btn"
                                                                    onClick={() => toggleDesc(item.id)}
                                                                >
                                                                    <FileText size={14} />
                                                                    <span>{descriptionPoints.length} point(s)</span>
                                                                    {isDescExpanded ? (
                                                                        <ChevronUp size={14} />
                                                                    ) : (
                                                                        <ChevronDown size={14} />
                                                                    )}
                                                                </button>
                                                            ) : (
                                                                <ul className="plm-description-bullets">
                                                                    {descriptionPoints.map((point, index) => (
                                                                        <li key={index}>
                                                                            {point.description}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )
                                                        ) : (
                                                            <span className="plm-empty-text">-</span>
                                                        )}
                                                    </td>
                                                    <td className="plm-col-amount">
                                                        {item.amount ? (
                                                            <span className="plm-amount-value">
                                                                ₹{item.amount}
                                                            </span>
                                                        ) : (
                                                            <span className="plm-empty-text">-</span>
                                                        )}
                                                    </td>
                                                    <td className="plm-col-documents">
                                                        {hasDocuments ? (
                                                            <button
                                                                className="plm-doc-toggle-btn"
                                                                onClick={() => toggleDocs(item.id)}
                                                            >
                                                                <span className="plm-doc-icon-wrap">
                                                                    <FileIcon size={14} />
                                                                </span>

                                                                <span className="plm-doc-count-badge">
                                                                    {item.documents.length}
                                                                </span>
                                                                {isDocsExpanded ? (
                                                                    <ChevronUp size={14} />
                                                                ) : (
                                                                    <ChevronDown size={14} />
                                                                )}
                                                            </button>
                                                        ) : (
                                                            <span className="plm-empty-text">-</span>
                                                        )}
                                                    </td>
                                                    <td className="plm-col-remarks">
                                                        {hasRemarks ? (
                                                            needsRemarkToggle ? (
                                                                <button
                                                                    className="plm-desc-toggle-btn"
                                                                    onClick={() => toggleRemark(item.id)}
                                                                    style={{ borderColor: '#c7d2fe' }}
                                                                >
                                                                    <span>View remark</span>
                                                                    {isRemarkExpanded ? (
                                                                        <ChevronUp size={14} />
                                                                    ) : (
                                                                        <ChevronDown size={14} />
                                                                    )}
                                                                </button>
                                                            ) : (
                                                                <div className="plm-remark-content">
                                                                    {item.remark}
                                                                </div>
                                                            )
                                                        ) : (
                                                            <span className="plm-empty-text">-</span>
                                                        )}
                                                    </td>
                                                    <td className="plm-col-action">
                                                        <div className="plm-action-buttons">
                                                            <UpdateProlongation project={project} i={item} getData={fetchAllData} />
                                                            {sessionStorage.getItem('sitemanagement_role') === "Admin" &&
                                                                <DeleteProlongation i={item} getData={fetchAllData} />
                                                            }
                                                        </div>
                                                    </td>
                                                </tr>

                                                {/* Expanded Description Row */}
                                                {isDescExpanded && hasDescription && needsDescToggle && (
                                                    <tr className="plm-desc-expanded-row">
                                                        <td colSpan="7">
                                                            <div className="plm-desc-expanded">
                                                                <div className="plm-desc-header">
                                                                    <FileText size={16} />
                                                                    <span>Description Points</span>
                                                                    <span className="plm-desc-count">{descriptionPoints.length}</span>
                                                                </div>
                                                                <ul className="plm-desc-list">
                                                                    {descriptionPoints.map((point, index) => (
                                                                        <li key={index}>
                                                                            {point.description}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}

                                                {/* Expanded Remarks Row */}
                                                {isRemarkExpanded && hasRemarks && needsRemarkToggle && (
                                                    <tr className="plm-remark-expanded-row">
                                                        <td colSpan="7">
                                                            <div className="plm-remark-expanded">
                                                                <div className="plm-desc-header">
                                                                    <span>💬</span>
                                                                    <span>Full Remarks</span>
                                                                </div>
                                                                <div className="plm-remark-expanded-content">
                                                                    {item.remark}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}

                                                {/* Expanded Documents Row */}
                                                {/* Expanded Documents Row */}
                                                {isDocsExpanded && hasDocuments && (
                                                    <tr className="plm-docs-expanded-row">
                                                        <td colSpan="7">
                                                            <div
                                                                className="plm-docs-expanded"
                                                                ref={el => docRefs.current[item.id] = el}
                                                            >
                                                                <div className="plm-docs-header">
                                                                    <div className="plm-docs-header-left">
                                                                        <div className="plm-docs-header-icon">
                                                                            <FileIcon size={16} />
                                                                        </div>
                                                                        <span className="plm-docs-header-title">Documents</span>
                                                                        <span className="plm-docs-header-count">
                                                                            {item.documents.length} file(s)
                                                                        </span>
                                                                    </div>
                                                                    <button
                                                                        className="plm-doc-zip-btn"
                                                                        onClick={() => handleDownloadAllDocuments(item)}
                                                                        disabled={downloadingZip[item.id]}
                                                                        title="Download all documents as ZIP"
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '6px',
                                                                            fontSize: '11px',
                                                                            padding: '5px 10px',
                                                                            borderRadius: '6px',
                                                                            border: '1px solid #c7d2fe',
                                                                            background: '#eef2ff',
                                                                            color: '#4338ca',
                                                                            cursor: downloadingZip[item.id] ? 'not-allowed' : 'pointer',
                                                                            opacity: downloadingZip[item.id] ? 0.6 : 1,
                                                                        }}
                                                                    >
                                                                        <Download size={13} />
                                                                        {downloadingZip[item.id] ? 'Zipping...' : 'Download All (ZIP)'}
                                                                    </button>
                                                                </div>
                                                                <div className="plm-docs-grid">
                                                                    {item.documents.map(doc => {
                                                                        // Get file extension and type
                                                                        const fileName = doc.document_name || 'Document';
                                                                        const lastDotIndex = fileName.lastIndexOf('.');
                                                                        const fileExt = lastDotIndex > 0 ? fileName.substring(lastDotIndex + 1).toLowerCase() : '';
                                                                        const fileNameWithoutExt = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;

                                                                        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico'].includes(fileExt);
                                                                        const isPdf = fileExt === 'pdf';
                                                                        const isWord = ['doc', 'docx'].includes(fileExt);
                                                                        const isExcel = ['xls', 'xlsx', 'csv'].includes(fileExt);
                                                                        const isPowerPoint = ['ppt', 'pptx'].includes(fileExt);
                                                                        const isZip = ['zip', 'rar', '7z', 'tar', 'gz'].includes(fileExt);

                                                                        // Get icon class
                                                                        let iconClass = 'other';
                                                                        let iconEmoji = '📄';
                                                                        if (isPdf) { iconClass = 'pdf'; iconEmoji = '📄'; }
                                                                        else if (isImage) { iconClass = 'image'; iconEmoji = '🖼️'; }
                                                                        else if (isWord) { iconClass = 'word'; iconEmoji = '📝'; }
                                                                        else if (isExcel) { iconClass = 'excel'; iconEmoji = '📊'; }
                                                                        else if (isPowerPoint) { iconClass = 'powerpoint'; iconEmoji = '📽️'; }
                                                                        else if (isZip) { iconClass = 'zip'; iconEmoji = '📦'; }

                                                                        // Format file size
                                                                        const formatFileSize = (bytes) => {
                                                                            if (!bytes) return '';
                                                                            const sizes = ['B', 'KB', 'MB', 'GB'];
                                                                            const i = Math.floor(Math.log(bytes) / Math.log(1024));
                                                                            return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
                                                                        };

                                                                        return (
                                                                            <div className="plm-doc-card" key={doc.id}>
                                                                                <div className={`plm-doc-card-icon ${iconClass}`}>
                                                                                    <span style={{ fontSize: '20px' }}>{iconEmoji}</span>
                                                                                </div>
                                                                                <div className="plm-doc-card-info">
                                                                                    <div className="plm-doc-card-name-wrapper">
                                                                                        <div
                                                                                            className="plm-doc-card-name"
                                                                                            title={doc.document_name || 'Document'}
                                                                                        >
                                                                                            {fileNameWithoutExt || 'Document'}
                                                                                        </div>
                                                                                        {fileExt && (
                                                                                            <span className="plm-doc-name-extension">
                                                                                                .{fileExt}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="plm-doc-card-meta">
                                                                                        <span className="plm-doc-type">
                                                                                            {fileExt || 'file'}
                                                                                        </span>
                                                                                        {doc.file_size && (
                                                                                            <span className="plm-doc-size">
                                                                                                • {formatFileSize(doc.file_size)}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="plm-doc-card-actions">
                                                                                    <button
                                                                                        className="plm-doc-action-btn view"
                                                                                        title="View Document"
                                                                                    >
                                                                                        <a
                                                                                            href={doc.document_file}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                        >
                                                                                            <Eye size={14} />
                                                                                        </a>
                                                                                    </button>
                                                                                    <button
                                                                                        className="plm-doc-action-btn download"
                                                                                        title="Download Document"
                                                                                        onClick={() => handleDownloadSingleDocument(doc)}
                                                                                        disabled={downloadingDoc[doc.id]}
                                                                                        style={{ opacity: downloadingDoc[doc.id] ? 0.5 : 1, cursor: downloadingDoc[doc.id] ? 'not-allowed' : 'pointer' }}
                                                                                    >
                                                                                        <Download size={14} />
                                                                                    </button>
                                                                                    {sessionStorage.getItem('sitemanagement_role') === "Admin" &&

                                                                                        <DeleteDocuments i={doc} getDocument={fetchAllData} />
                                                                                    }
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="plm-empty-state">
                        <div className="plm-empty-icon">📭</div>
                        <div className="plm-empty-message">
                            {activeFilter !== 'All'
                                ? `No ${activeFilter} records found`
                                : 'No prolongation or deviation records found'}
                        </div>
                        {activeFilter !== 'All' && (
                            <button
                                className="plm-empty-clear-btn"
                                onClick={() => handleFilter('All')}
                            >
                                Show all records
                            </button>
                        )}
                    </div>
                )}

                <div className="plm-modal-footer">
                    <button className="plm-footer-close-btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default ProlongationModal;
